/**
 * Primitive.tsx — the elicitation instruments. Ten interaction widgets render all
 * sixteen approved primitives; each emits a single EvidenceItem (summary + an
 * explicit boundary on what it does NOT establish + raw content) which the session
 * commits via `evidence.capture`. The specimen frame, reflective echo, evidence
 * block, and review-affordance bar are drawn from egae-component-library §01.
 */
import { useState } from "react";
import type { PrimitiveDefinition, WidgetKind } from "../../domain/primitives.ts";
import { EvidenceOut, Boundary } from "../lib/format.tsx";
import { AutoTextarea } from "../lib/auto-textarea.tsx";
import { StaticBlocks, CanvasEditor } from "../genui/blocks.tsx";
import type { BlockData } from "../../genui/blocks.ts";
import { useT } from "../lib/i18n.tsx";
import { Specimen, Echo, StimulusCtx, boundaryFor, firstSentence, safeBlocks } from "./specimen.tsx";
import type { EmitPayload, WidgetProps } from "./specimen.tsx";
import { BUSINESS_METHOD_WIDGETS } from "../business/methods.tsx";

export type { EmitPayload, WidgetProps } from "./specimen.tsx";

// ── Widget: narrative (open-narrative, reflective-summary, artifact-walkthrough,
//    contextual-inquiry, self-explanation) ───────────────────────────────────
function NarrativeWidget({ primitive, emit }: WidgetProps): JSX.Element {
  const t = useT();
  const [text, setText] = useState("");
  const [done, setDone] = useState(false);
  const summary = firstSentence(text);
  return (
    <Specimen
      p={primitive}
      echo={done ? <Echo>{t("prim.narr.echo")}<b>{summary}</b>{t("prim.narr.echo2")}</Echo> : undefined}
      evidence={
        done ? (
          <EvidenceOut
            rows={[
              ["elicitationPrimitive", primitive.slug],
              ["normalizedSummary", `"${summary}"`],
              ["evidenceBoundary", <Boundary key="b">{boundaryFor(primitive)}</Boundary>],
              ["interpretationStatus", "uninterpreted"],
            ]}
          />
        ) : undefined
      }
    >
      <AutoTextarea rows={3} value={text} placeholder={t("prim.narr.ph")} onChange={(e) => setText(e.target.value)} />
      <div className="btn-row">
        <button
          className="btn primary"
          disabled={!text.trim()}
          onClick={() => {
            setDone(true);
            emit({ summary: text.trim(), boundary: boundaryFor(primitive), rawContent: { text: text.trim() } });
          }}
        >
          {t("prim.reflect")}
        </button>
      </div>
    </Specimen>
  );
}

// ── Widget: incident (critical-incident-capture) ────────────────────────────
function IncidentWidget({ primitive, emit }: WidgetProps): JSX.Element {
  const t = useT();
  const [situation, setSituation] = useState("");
  const [action, setAction] = useState("");
  const [why, setWhy] = useState("");
  const [done, setDone] = useState(false);
  const ready = situation.trim() && action.trim();
  const summary = `${action.trim()} — because ${why.trim() || "(unstated)"}`;
  return (
    <Specimen
      p={primitive}
      evidence={
        done ? (
          <EvidenceOut
            rows={[
              ["elicitationPrimitive", primitive.slug],
              ["situation", `"${firstSentence(situation)}"`],
              ["action", `"${firstSentence(action)}"`],
              ["rationale", `"${firstSentence(why) || "(unstated)"}"`],
              ["evidenceBoundary", <Boundary key="b">single incident · not yet doctrine · review required</Boundary>],
            ]}
          />
        ) : undefined
      }
    >
      {[
        ["situation", situation, setSituation, t("prim.inc.situation.ph"), t("prim.inc.situation")],
        ["action", action, setAction, t("prim.inc.action.ph"), t("prim.inc.action")],
        ["why", why, setWhy, t("prim.inc.why.ph"), t("prim.inc.why")],
      ].map(([k, v, set, ph, label]) => (
        <div className="field" key={k as string}>
          <div className="field-label">
            <span>{label as string}</span>
          </div>
          <AutoTextarea rows={2} value={v as string} placeholder={ph as string} onChange={(e) => (set as (s: string) => void)(e.target.value)} />
        </div>
      ))}
      <div className="btn-row">
        <button
          className="btn primary"
          disabled={!ready}
          onClick={() => {
            setDone(true);
            emit({ summary, boundary: "single incident · not yet doctrine · review required", rawContent: { situation, action, why } });
          }}
        >
          {t("prim.recordIncident")}
        </button>
      </div>
    </Specimen>
  );
}

// ── Widget: best-worst ──────────────────────────────────────────────────────
// Neutral fallback ONLY when offline/synthesis fails — a live model supplies items
// that fit the question's topic (see QuestionStimulus.items).
const BW_FALLBACK = ["Speed", "Quality of outcome", "Cost discipline", "Trust & relationship", "Consistency of rules"];
function BestWorstWidget({ primitive, emit, stimulus }: WidgetProps): JSX.Element {
  const t = useT();
  const BW_ITEMS = stimulus?.items && stimulus.items.length >= 2 ? [...stimulus.items] : BW_FALLBACK;
  const [best, setBest] = useState<number | null>(null);
  const [worst, setWorst] = useState<number | null>(null);
  const ready = best !== null && worst !== null;
  return (
    <Specimen
      p={primitive}
      evidence={
        ready ? (
          <EvidenceOut
            rows={[
              ["elicitationPrimitive", primitive.slug],
              ["mostProtected", `"${BW_ITEMS[best!]}"`],
              ["leastProtected", `"${BW_ITEMS[worst!]}"`],
              ["evidenceBoundary", <Boundary key="b">one borderline context · not a global ranking</Boundary>],
            ]}
          />
        ) : undefined
      }
    >
      {BW_ITEMS.map((item, i) => (
        <div className="bw-row" key={item}>
          <div className="bw-pick">
            <button
              className={`best ${best === i ? "on" : ""}`}
              onClick={() => {
                setWorst((w) => (w === i ? null : w));
                setBest((b) => (b === i ? null : i));
              }}
            >
              best
            </button>
            <button
              className={`worst ${worst === i ? "on" : ""}`}
              onClick={() => {
                setBest((b) => (b === i ? null : b));
                setWorst((w) => (w === i ? null : i));
              }}
            >
              worst
            </button>
          </div>
          <div className="bw-item">{item}</div>
        </div>
      ))}
      <div className="btn-row">
        <button
          className="btn primary"
          disabled={!ready}
          onClick={() =>
            emit({
              summary: `Protects most: ${BW_ITEMS[best!]}; least: ${BW_ITEMS[worst!]}`,
              boundary: "one borderline context · not a global ranking",
              rawContent: { items: BW_ITEMS, best: BW_ITEMS[best!], worst: BW_ITEMS[worst!] },
            })
          }
        >
          {t("prim.recordPriority")}
        </button>
      </div>
    </Specimen>
  );
}

// ── Widget: triad ───────────────────────────────────────────────────────────
const TRIAD_FALLBACK = [
  { t: "Case A", d: "A time you chose the principled option." },
  { t: "Case B", d: "A time you chose the pragmatic option." },
  { t: "Case C", d: "A time you chose the cautious option." },
];
function TriadWidget({ primitive, emit, stimulus }: WidgetProps): JSX.Element {
  const t = useT();
  const TRIAD = stimulus?.triad && stimulus.triad.length === 3 ? stimulus.triad.map((x) => ({ t: x.label, d: x.detail })) : TRIAD_FALLBACK;
  const [odd, setOdd] = useState<number | null>(null);
  const [pole, setPole] = useState("");
  return (
    <Specimen
      p={primitive}
      echo={odd !== null ? <Echo>Name what the two alike incidents share — that becomes <b>your</b> construct pole, not ours.</Echo> : undefined}
      evidence={
        odd !== null && pole.trim() ? (
          <EvidenceOut
            rows={[
              ["elicitationPrimitive", primitive.slug],
              ["oddOneOut", TRIAD[odd]!.t],
              ["construct", `"${pole.trim()}"`],
              ["evidenceBoundary", <Boundary key="b">one triad reveals one axis · not the whole grid</Boundary>],
            ]}
          />
        ) : undefined
      }
    >
      <div className="triad">
        {TRIAD.map((card, i) => (
          <div key={card.t} className={`tri-card ${odd === i ? "odd" : odd !== null ? "alike" : ""}`} onClick={() => setOdd((o) => (o === i ? null : i))}>
            <span className="tri-tag">
              {card.t}
              {odd === i ? " · odd one out" : odd !== null ? " · alike" : ""}
            </span>
            {card.d}
          </div>
        ))}
      </div>
      {odd !== null ? (
        <div className="field" style={{ marginTop: 12 }}>
          <input className="field-input" placeholder="Name the construct (e.g. trust-call ↔ economics-call)" value={pole} onChange={(e) => setPole(e.target.value)} />
        </div>
      ) : null}
      <div className="btn-row">
        <button
          className="btn primary"
          disabled={odd === null || !pole.trim()}
          onClick={() =>
            emit({
              summary: `Construct: ${pole.trim()} (odd one out: ${TRIAD[odd!]!.t})`,
              boundary: "one triad reveals one axis · not the whole grid",
              rawContent: { oddOneOut: TRIAD[odd!]!.t, construct: pole.trim() },
            })
          }
        >
          {t("prim.recordConstruct")}
        </button>
      </div>
    </Specimen>
  );
}

// ── Widget: ladder ──────────────────────────────────────────────────────────
const LADDER_PROMPTS = ["Why does that matter to you?", "And why does THAT matter?", "What would be at stake if you didn't?", "What does that protect, underneath it all?"];
function LadderWidget({ primitive, emit, seed }: WidgetProps): JSX.Element {
  const t = useT();
  // The first rung is the surface "rule" the participant already expressed; the AI
  // supplies it (grounded in the evidence so far) so the climb starts from their words,
  // not a canned example. Remounted per question (keyed in the session), so this is fresh.
  const [rungs, setRungs] = useState<{ lvl: string; txt: string }[]>(() => [
    { lvl: "rule", txt: seed?.trim() || t("prim.ladder.seedFallback") },
  ]);
  const [input, setInput] = useState("");
  const lp = rungs.length - 1;
  const terminal = rungs.length >= 5;
  return (
    <Specimen
      p={primitive}
      evidence={
        terminal ? (
          <EvidenceOut
            rows={[
              ["elicitationPrimitive", primitive.slug],
              ["chain", rungs.map((r) => r.txt).join(" → ")],
              ["terminalValue", `"${rungs[rungs.length - 1]!.txt}"`],
              ["evidenceBoundary", <Boundary key="b">articulated value ≠ value always enacted under pressure</Boundary>],
            ]}
          />
        ) : undefined
      }
    >
      <div className="ladder">
        {rungs.map((r, i) => (
          <div key={i} className={`rung ${i === rungs.length - 1 && i >= 4 ? "terminal" : ""}`}>
            <span className="lvl">{r.lvl}</span>
            <div className="txt">{r.txt}</div>
          </div>
        ))}
      </div>
      {!terminal ? (
        <>
          <div className="ladder-prompt">{LADDER_PROMPTS[Math.min(lp, 3)]}</div>
          <input className="field-input" value={input} placeholder="Because…" onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => {
            if (e.key === "Enter" && input.trim()) {
              const lvls = ["→ because", "→ because", "→ so that", "▲ value"];
              setRungs((rs) => [...rs, { lvl: lvls[Math.min(rs.length - 1, 3)]!, txt: input.trim() }]);
              setInput("");
            }
          }} />
        </>
      ) : (
        <div className="ladder-prompt">You've reached a <b style={{ color: "var(--phosphor)" }}>terminal value</b>. Stop — climbing further just paraphrases.</div>
      )}
      <div className="btn-row">
        {terminal ? (
          <button
            className="btn primary"
            onClick={() =>
              emit({
                summary: `Value chain: ${rungs.map((r) => r.txt).join(" → ")}`,
                boundary: "articulated value ≠ value always enacted under pressure",
                rawContent: { chain: rungs },
              })
            }
          >
            {t("prim.recordChain")}
          </button>
        ) : (
          <span className="mini">Add at least four rungs to reach a terminal value.</span>
        )}
      </div>
    </Specimen>
  );
}

// ── Widget: semantic differential ───────────────────────────────────────────
function DifferentialWidget({ primitive, emit, stimulus }: WidgetProps): JSX.Element {
  const t = useT();
  const left = stimulus?.axis?.left?.trim() || "follow the rule";
  const right = stimulus?.axis?.right?.trim() || "use judgment";
  const axisLabel = `${left} ↔ ${right}`;
  const [v, setV] = useState(58);
  const lean = v < 40 ? `leans ${left}` : v > 60 ? `leans ${right}` : "balanced";
  const [done, setDone] = useState(false);
  return (
    <Specimen
      p={primitive}
      evidence={
        done ? (
          <EvidenceOut
            rows={[
              ["elicitationPrimitive", primitive.slug],
              ["axis", `"${axisLabel}"`],
              ["position", `${v}/100 (${lean})`],
              ["evidenceBoundary", <Boundary key="b">a default tendency under uncertainty · not a hard rule</Boundary>],
            ]}
          />
        ) : undefined
      }
    >
      <div className="sd-poles">
        <span className="l">{left}</span>
        <span className="r">{right}</span>
      </div>
      <input type="range" min={0} max={100} value={v} className="sd" onChange={(e) => setV(Number(e.target.value))} />
      <div className="sd-read">
        position · <b>{v}</b> / 100 — {lean}
      </div>
      <div className="btn-row">
        <button
          className="btn primary"
          onClick={() => {
            setDone(true);
            emit({ summary: `Default posture ${v}/100 — ${lean}`, boundary: "a default tendency under uncertainty · not a hard rule", rawContent: { axis: axisLabel, position: v, lean } });
          }}
        >
          {t("prim.recordPosture")}
        </button>
      </div>
    </Specimen>
  );
}

// ── Widget: scenario vignette ───────────────────────────────────────────────
const VIG_FALLBACK = [
  { m: "A", t: "Act now — the most likely correct move." },
  { m: "B", t: "Gather more context first, then act." },
  { m: "C", t: "Escalate to a human — the tension needs a person." },
];
function ScenarioWidget({ primitive, emit, stimulus }: WidgetProps): JSX.Element {
  const t = useT();
  const VIG_OPTS = stimulus?.options && stimulus.options.length >= 2 ? stimulus.options.map((o) => ({ m: o.marker, t: o.text })) : VIG_FALLBACK;
  const vignette = stimulus?.scenario?.trim() || "A borderline case arises and the information is incomplete. What should the agent do?";
  const [sel, setSel] = useState<number | null>(null);
  const [caveat, setCaveat] = useState("");
  return (
    <Specimen
      p={primitive}
      evidence={
        sel !== null ? (
          <EvidenceOut
            rows={[
              ["elicitationPrimitive", primitive.slug],
              ["chosenResponse", `option ${VIG_OPTS[sel]!.m}`],
              ["decision", `"${VIG_OPTS[sel]!.t}"`],
              ["caveat", caveat.trim() ? `"${caveat.trim()}"` : "(none recorded)"],
              ["evidenceBoundary", <Boundary key="b">hypothetical vignette · validate against real cases before doctrine</Boundary>],
            ]}
          />
        ) : undefined
      }
    >
      <div className="vig">{vignette}</div>
      {VIG_OPTS.map((o, i) => (
        <button key={o.m} className={`opt ${sel === i ? "sel" : ""}`} onClick={() => setSel(i)}>
          <span className="mk">{o.m}</span>
          {o.t}
        </button>
      ))}
      <div className="field" style={{ marginTop: 8 }}>
        <input className="field-input" placeholder="Add a caveat that should travel with this decision…" value={caveat} onChange={(e) => setCaveat(e.target.value)} />
      </div>
      <div className="btn-row">
        <button
          className="btn primary"
          disabled={sel === null}
          onClick={() =>
            emit({
              summary: `Chose ${VIG_OPTS[sel!]!.t}${caveat.trim() ? ` (caveat: ${caveat.trim()})` : ""}`,
              boundary: "hypothetical vignette · validate against real cases before doctrine",
              rawContent: { option: VIG_OPTS[sel!]!.m, decision: VIG_OPTS[sel!]!.t, caveat: caveat.trim() },
            })
          }
        >
          {t("prim.recordDecision")}
        </button>
      </div>
    </Specimen>
  );
}

// ── Widget: card sort / q-sort ──────────────────────────────────────────────
const CS_FALLBACK = ["A routine, low-stakes case", "A high-value but ambiguous case", "A case with possible harm", "A novel case with no precedent", "A case peers disagree on"];
const BUCKETS_FALLBACK: { id: string; label: string }[] = [
  { id: "agent", label: "agent decides" },
  { id: "human", label: "needs a human" },
  { id: "escalate", label: "escalate" },
];
function CardSortWidget({ primitive, emit, stimulus, constrained }: WidgetProps & { constrained?: boolean }): JSX.Element {
  const t = useT();
  const CS_CARDS = stimulus?.cards && stimulus.cards.length >= 2 ? [...stimulus.cards] : (stimulus?.items && stimulus.items.length >= 2 ? [...stimulus.items] : CS_FALLBACK);
  const BUCKETS = stimulus?.buckets && stimulus.buckets.length >= 2 ? [...stimulus.buckets] : BUCKETS_FALLBACK;
  const [state, setState] = useState<Record<number, string>>({});
  const [active, setActive] = useState<number | null>(null);
  const placed = Object.keys(state).length;
  const lanes = (b: string): string[] => Object.entries(state).filter(([, v]) => v === b).map(([i]) => CS_CARDS[Number(i)]!);
  const byBucket = (): Record<string, string[]> => Object.fromEntries(BUCKETS.map((b) => [b.id, lanes(b.id)]));
  return (
    <Specimen
      p={primitive}
      evidence={
        placed > 0 ? (
          <EvidenceOut
            rows={[
              ["elicitationPrimitive", primitive.slug],
              ...BUCKETS.map((b) => [b.label, `[${lanes(b.id).join(" · ")}]`] as [string, string]),
              ["sorted", `${placed}/${CS_CARDS.length}`],
              ["evidenceBoundary", <Boundary key="b">card set ≠ exhaustive world · lanes are participant-named</Boundary>],
            ]}
          />
        ) : undefined
      }
    >
      <div className="cs-pool">
        {CS_CARDS.map((c, i) =>
          state[i] ? null : (
            <div key={c} className={`cs-card ${active === i ? "active" : ""}`} onClick={() => setActive((a) => (a === i ? null : i))}>
              {c}
            </div>
          ),
        )}
      </div>
      <div className="cs-buckets">
        {BUCKETS.map((b) => (
          <div
            key={b.id}
            className="cs-bucket"
            data-b={b.id}
            onClick={() => {
              if (active === null) return;
              setState((s) => ({ ...s, [active]: b.id }));
              setActive(null);
            }}
          >
            <div className="cs-bhead">{b.label}{constrained ? " · max 2" : ""}</div>
            {lanes(b.id).map((c, idx) => {
              const cardIdx = CS_CARDS.indexOf(c);
              return (
                <div key={idx} className="cs-chip" onClick={(e) => { e.stopPropagation(); setState((s) => { const n = { ...s }; delete n[cardIdx]; return n; }); }}>
                  {c} ✕
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div className="btn-row">
        <button
          className="btn primary"
          disabled={placed === 0}
          onClick={() =>
            emit({
              summary: `Sorted into ${BUCKETS.map((b) => `${b.label}: [${lanes(b.id).join(", ")}]`).join("; ")}`,
              boundary: "card set ≠ exhaustive world · lanes are participant-named",
              rawContent: { buckets: byBucket() },
            })
          }
        >
          {t("prim.recordSort")}
        </button>
      </div>
    </Specimen>
  );
}

// ── Widget: decision reconstruction (steps) ─────────────────────────────────
function StepsWidget({ primitive, emit }: WidgetProps): JSX.Element {
  const t = useT();
  const [steps, setSteps] = useState<string[]>([]);
  const [draft, setDraft] = useState("");
  return (
    <Specimen
      p={primitive}
      evidence={
        steps.length > 0 ? (
          <EvidenceOut
            rows={[
              ["elicitationPrimitive", primitive.slug],
              ["decisionTrace", steps.map((s, i) => `${i + 1}. ${s}`).join("  ")],
              ["evidenceBoundary", <Boundary key="b">retrospective coherence the moment didn't have · review required</Boundary>],
            ]}
          />
        ) : undefined
      }
    >
      <div className="steps-list">
        {steps.map((s, i) => (
          <div className="step-row" key={i}>
            <span className="sn">{String(i + 1).padStart(2, "0")}</span>
            <span>{s}</span>
          </div>
        ))}
      </div>
      <div className="row">
        <input
          className="field-input"
          placeholder="Next step: a cue, an option weighed, the deciding factor…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && draft.trim()) {
              setSteps((s) => [...s, draft.trim()]);
              setDraft("");
            }
          }}
        />
      </div>
      <div className="btn-row">
        <button
          className="btn primary"
          disabled={steps.length === 0}
          onClick={() => emit({ summary: `Decision trace: ${steps.join(" → ")}`, boundary: "retrospective coherence the moment didn't have · review required", rawContent: { steps } })}
        >
          {t("prim.recordTrace")}
        </button>
      </div>
    </Specimen>
  );
}

// ── Widget: workload check ──────────────────────────────────────────────────
function WorkloadWidget({ primitive, emit }: WidgetProps): JSX.Element {
  const t = useT();
  const [level, setLevel] = useState<"low" | "medium" | "high" | null>(null);
  return (
    <Specimen
      p={primitive}
      evidence={
        level ? (
          <EvidenceOut
            rows={[
              ["elicitationPrimitive", primitive.slug],
              ["loadState", level],
              ["guidance", level === "high" ? "drop to recognition; defer reconstruction" : level === "medium" ? "recognition before recall" : "recall & reconstruction are safe"],
              ["evidenceBoundary", <Boundary key="b">self-reported load · may under-report fatigue</Boundary>],
            ]}
          />
        ) : undefined
      }
    >
      <div className="workload-set">
        {(["low", "medium", "high"] as const).map((l) => (
          <button key={l} data-l={l} className={level === l ? "on" : ""} onClick={() => { setLevel(l); emit({ summary: `Cognitive load: ${l}`, boundary: "self-reported load · may under-report fatigue", rawContent: { load: l } }); }}>
            {l}
          </button>
        ))}
      </div>
    </Specimen>
  );
}

// ── Widget: generative canvas ───────────────────────────────────────────────
// The generic instrument: the participant fulfils the step by EDITING a structured
// canvas (seeded by the AI) rather than answering a fixed-option question. Reuses the
// GenUI block editor, so a Value Proposition Canvas, a workflow map, or any structured
// artifact is co-built with the same affordances the assistant uses on the scratchpad.
function canvasSummary(blocks: readonly BlockData[]): string {
  const titles: string[] = [];
  const walk = (b: BlockData): void => {
    if (b.kind === "group" && b.title) titles.push(b.title);
    else if ((b.kind === "heading" || b.kind === "fields" || b.kind === "list" || b.kind === "table" || b.kind === "diagram") && "title" in b && b.title) titles.push(b.title);
    if (b.kind === "group") b.children.forEach(walk);
  };
  blocks.forEach(walk);
  return titles.length ? `Canvas shaped: ${titles.slice(0, 6).join(" · ")}` : "Canvas shaped (untitled regions)";
}
function CanvasWidget({ primitive, emit, stimulus, ws }: WidgetProps): JSX.Element {
  const t = useT();
  const [blocks, setBlocks] = useState<readonly BlockData[]>(() => safeBlocks(stimulus?.canvas));
  const [done, setDone] = useState(false);
  const ready = blocks.length > 0;
  return (
    <Specimen
      p={primitive}
      evidence={
        done ? (
          <EvidenceOut
            rows={[
              ["elicitationPrimitive", primitive.slug],
              ["canvas", `${blocks.length} top-level region(s)`],
              ["evidenceBoundary", <Boundary key="b">a co-built artifact · the seeded structure may anchor · review required</Boundary>],
            ]}
          />
        ) : undefined
      }
    >
      <CanvasEditor ws={ws} blocks={blocks} onChange={setBlocks} />
      <div className="btn-row">
        <button
          className="btn primary"
          disabled={!ready}
          onClick={() => {
            setDone(true);
            emit({ summary: canvasSummary(blocks), boundary: "a co-built artifact · the seeded structure may anchor · review required", rawContent: { canvas: blocks } });
          }}
        >
          {t("prim.recordCanvas")}
        </button>
      </div>
    </Specimen>
  );
}

const WIDGETS: Record<WidgetKind, (p: WidgetProps) => JSX.Element> = {
  narrative: NarrativeWidget,
  incident: IncidentWidget,
  bestworst: BestWorstWidget,
  triad: TriadWidget,
  ladder: LadderWidget,
  differential: DifferentialWidget,
  scenario: ScenarioWidget,
  cardsort: (p) => <CardSortWidget {...p} />,
  qsort: (p) => <CardSortWidget {...p} constrained />,
  steps: StepsWidget,
  workload: WorkloadWidget,
  canvas: CanvasWidget,
};

export function Primitive({ primitive, emit, promptOverride, seed, stimulus, ws, packSlug }: WidgetProps & { packSlug?: string | undefined }): JSX.Element {
  // Pack-scoped resolution: in a Business Operating Model session each method renders its
  // purpose-built typed component; every other pack keeps the generic widget. (generative-
  // canvas has no business override here — its generic `canvas` widget already renders the
  // typed business-canvas, so it stays custom too.)
  const business = packSlug === "business-operating-model" ? BUSINESS_METHOD_WIDGETS[primitive.slug] : undefined;
  const Widget = business ?? WIDGETS[primitive.widget];
  // Inject the AI-written question by overriding the primitive's default prompt;
  // every widget reads `primitive.prompt` through <Specimen>, so this flows through.
  // (The stimulus body, when present, takes precedence over this in <Specimen>.)
  const effective = promptOverride && promptOverride.trim() ? { ...primitive, prompt: promptOverride } : primitive;
  return (
    <StimulusCtx.Provider value={{ stimulus, ws }}>
      <Widget primitive={effective} emit={emit} seed={seed} stimulus={stimulus} ws={ws} />
    </StimulusCtx.Provider>
  );
}
